import { Auth0Provider } from "@auth0/auth0-react";
import { LicenseInfo } from "@mui/x-license";
import React, { useEffect } from "react";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from "react-router-dom";

import RouterErrorElement from "components/RouterErrorElement";
import UserNotificationSubscription from "components/UserNotificationSubscription";
import StyledToastContainer from "components/shared/Toast";
import ConfirmationDialogContainer from "contexts/confirmationDialog/ConfirmationDialogContainer";
import ModalContainer from "contexts/modal/ModalContainer";
import Auth0UserSessionContainer from "contexts/userSession/Auth0UserSessionContainer";
import { STORAGE_CHUNK_RELOAD_FLAG } from "globalConstants/storage";
import { SELECT_WORKSPACE_PATH } from "routes/paths";
import { auth0Options } from "settings/auth0";
import configureSentry from "settings/sentry";

import { AuthorizedApolloProvider } from "./AuthorizedApolloProvider";
import { ExternalApolloProvider } from "./ExternalApolloProvider";
import ScrollToTop from "./components/shared/ScrollToTop";
import { externalRouteElements, internalRouteElements } from "./routes";
import ThemeConfig from "./theme";

configureSentry();

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_PRO_KEY);

const BaseApp = (): JSX.Element => (
  <ThemeConfig>
    <StyledToastContainer />
    <ScrollToTop />
    <ConfirmationDialogContainer>
      <ModalContainer>
        <Outlet />
      </ModalContainer>
    </ConfirmationDialogContainer>
  </ThemeConfig>
);

const Auth0LayoutApp = (): JSX.Element => {
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.removeItem(STORAGE_CHUNK_RELOAD_FLAG);
  }, []);

  return (
    <Auth0Provider
      {...auth0Options}
      onRedirectCallback={() => navigate(SELECT_WORKSPACE_PATH)}
    >
      <Auth0UserSessionContainer>
        <AuthorizedApolloProvider>
          <BaseApp />
          <UserNotificationSubscription />
        </AuthorizedApolloProvider>
      </Auth0UserSessionContainer>
    </Auth0Provider>
  );
};

const ExternalLayoutApp = (): JSX.Element => (
  <ExternalApolloProvider>
    <BaseApp />
  </ExternalApolloProvider>
);

const AppRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        element={<Auth0LayoutApp />}
        errorElement={<RouterErrorElement />}
      >
        {internalRouteElements}
      </Route>
      <Route
        element={<ExternalLayoutApp />}
        errorElement={<RouterErrorElement />}
      >
        {externalRouteElements}
      </Route>
    </>,
  ),
);

export const App = (): JSX.Element => <RouterProvider router={AppRouter} />;
